import {ErrorBag, RestModelError} from "@oilstone/rest-model-repository";
import {inject, provide, reactive} from "vue";
import Kernel from "@oilstone/kernel";
import {accessesData, hasEvents, mutatesData, persistsData} from "@/services/data-layer/composers";
import Navigation from "@/services/navigation";
import {useRoute} from 'vue-router'

export default () => {
    return {
        setup() {
            const type = inject('type');
            const uuid = inject('uuid');
            const settings = Kernel.resolve('resourceRegistry').find(type);
            const {dispatchChanges, prepareUpdate} = mutatesData();
            const {item} = accessesData();
            const {listen, fire} = hasEvents();
            const {overwrite, destroy} = persistsData();
            const id = useRoute().params.id;
            const mode = id === 'new' ? 'create' : 'update';
            const errors = reactive(new ErrorBag());

            provide('mode', mode);
            provide('errors', errors);

            return {
                type,
                uuid,
                listen,
                fire,
                dispatchChanges,
                prepareUpdate,
                saveRecord: overwrite,
                destroyRecord: destroy,
                getRecord: item,
                config: settings.config,
                schema: settings.repository.schema,
                mode,
                errors,
            }
        },

        emits: ['loaded'],

        computed: {
            pageTitle() {
                return (this.mode === 'create' ? 'Create a new ' : 'Update ') + this.config.aliases.lowerSingular;
            },
        },

        methods: {
            close() {
                Navigation.push({
                    name: 'admin-resource',
                    params: {type: this.type},
                    query: this.$route.query
                });
            },

            publish() {
                this.fire('publishing');

                this.persist({
                    isPublished: true,
                    hasUnpublishedChanges: false,
                }).then(resource => {
                    if (resource) {
                        this.fire('masterPublished');
                        this.close();
                    }
                });
            },

            unpublish() {
                this.fire('unpublishing');

                return this.persist({
                    isPublished: false,
                    hasUnpublishedChanges: false,
                }).then(resource => {
                    if (resource) {
                        this.fire('masterSaved');
                        this.close();
                    }
                });
            },

            saveClose() {
                this.fire('savedAndClosing');

                return this.persist({
                    hasUnpublishedChanges: true,
                }).then(resource => {
                    if (resource) {
                        this.fire('masterSaved');
                        this.close();
                    }
                });
            },

            save() {
                this.fire('saving');

                return this.persist({
                    hasUnpublishedChanges: true,
                }).then(resource => {
                    if (resource) {
                        this.fire('masterSaved');
                    }
                });
            },

            persist(append = {}) {
                this.errors.empty();

                return new Promise((resolve, reject) => {
                    this.dispatchChanges();

                    const data = Object.assign({}, this.getRecord(this.uuid).value, append);

                    this.saveRecord(data).then(resource => {
                        resolve(resource);
                    }, error => {
                        if (error instanceof RestModelError) {
                            this.errors.replace(error.getBag());
                            this.fire('invalidResource');
                            resolve(null);
                        }

                        reject(error);
                    });
                });
            },

            destroy() {
                this.fire('destroying');

                this.destroyRecord().then(() => {
                    Navigation.push({
                        name: 'admin-resource',
                        params: {type: this.type},
                        query: this.$route.query
                    });
                })
            }
        },

        mounted() {
            // Apply any filters to the new resource
            if (this.mode === 'create') {
                for (const paramType in this.$route.query) {
                    this.prepareUpdate(paramType, this.$route.query[paramType] || null);
                }
            }
        }
    }
}
